/**
 * Life Blueprint V2.9.9 — Migration Batch 07-2 · M04 minimum Sheet28 engine
 *   (BZ_五行强弱 / 08_五行强弱 · rows 5..9 inputs, 13..28 weighted matrix,
 *    32..37 five-element totals/ratios, 42..44 support flags, 45 score,
 *    50..55 day-master / status / need / tie / boundary).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — MINIMUM (Batch 07-2 authorised subset ONLY)
 *   INPUTS
 *     B5:D9  four-pillar audited inputs (year/month/day/hour stem + branch,
 *            plus B9 = month branch for 月令 coefficient lookup)
 *   WEIGHTED MATRIX
 *     B13:F28  16 rows × 5 columns
 *       Rows 13..16 = 年干 / 月干 / 日干 / 时干 (天干)
 *       Rows 17..19 = 年支 本气/中气/余气 (地支藏干)
 *       Rows 20..22 = 月支 本气/中气/余气
 *       Rows 23..25 = 日支 本气/中气/余气
 *       Rows 26..28 = 时支 本气/中气/余气
 *     Column B = 天干/藏干 glyph
 *     Column C = 五行 glyph
 *     Column D = 基础权重 (天干 static 5; 藏干 from DB_藏干 col 6, IFERROR 0)
 *     Column E = 月令系数 (VLOOKUP $B$9 in DB_月令权重; if 藏干 D=0 → E=0)
 *     Column F = 加权得分 (D * E)
 *   TOTALS / RATIOS
 *     B32..B36 = SUMIF over 五行 ∈ {木,火,土,金,水}
 *     C32..C36 = IF(B37=0, 0, Bi / B37)
 *     B37      = SUM(B32:B36)
 *   SUPPORT FLAGS
 *     B42 得令：月支本气五行 ∈ {日主, 印(生我)} → "得令" | "不得令"
 *     B43 得地：任一 C17..C28 藏干五行 ∈ {日主, 印} → "得地" | "不得地"
 *     B44 得助：任一 C13/C14/C16 天干五行 ∈ {日主, 印} → "得助" | "不得助"
 *   SCORE
 *     B45 = ROUND(B51*100, 1)
 *           + (B42="得令" ? 10 : 0)
 *           + (B43="得地" ? 10 : 0)
 *           + (B44="得助" ? 5  : 0)
 *   DAY-MASTER
 *     B50 = C15 (day stem 五行)
 *     B51 = IFERROR(VLOOKUP(B50, A32:C36, 3, FALSE), 0)  (day-master 占比)
 *   STATUS / NEED / TIE / BOUNDARY
 *     B52  = IF(B45<35,"偏弱", IF(B45>65,"偏强", "平衡"))
 *     B53  = IF(B37=0,"", "补" & INDEX(A32:A36, MATCH(MIN(B32:B36), B32:B36, 0)))
 *     B54  = IF(B37=0, "",
 *                IF(COUNTIF(B32:B36, MIN(B32:B36)) > 1,
 *                   "⚠️ 并列提示：<N>个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。",
 *                   "无并列"))
 *     B55  = 'BZ_自动排盘'!B71  (upstream Sheet27 B71 boundary summary — passthrough)
 *
 * OUT OF SCOPE for Batch 07-2 (MUST NOT be implemented here)
 *   A40:B41 生扶 / 克泄耗 五行 descriptors
 *   Any Sheet28 row beyond the enumerated cells above.
 *   Any wiring into the production V2.9.9 pipeline or UI.
 *
 * REFERENCE-DATA REUSE (single sources of truth, no duplication)
 *   DB_天干             → M02 Batch 01 `tianGanWuXing`
 *   DB_地支 (本气五行)   → M02 Batch 01 `diZhiBenQiWuXing`
 *   DB_藏干             → M02 Batch 02 `cangGanLookupBy` / `cangGanRowsForDiZhi`
 *                         via Batch 06 compatibility layer (identical rows)
 *   DB_月令权重         → M02 Batch 07-1 `yueLingWeightByBranchAndElement`
 *   DB_十神 A:E 五行辅助 → M02 Batch 03 `fiveElementAuxLookup`
 *
 * IFERROR SEMANTICS
 *   Sheet28 wraps every hidden-stem VLOOKUP with IFERROR(...,"") for B17:C28
 *   and IFERROR(...,0) for D17:D28. The M02 authoritative helpers throw
 *   MISSING_REFERENCE_KEY for absent keys — this composer catches ONLY that
 *   coded error and translates to the audited "" / 0 defaults. The E-column
 *   (月令系数) inherits the audited semantic `IF(Di=0, 0, ...)` so a missing
 *   藏干 row cascades cleanly to a 0 加权得分 (F-column).
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Same audited inputs → byte-wise
 *   identical output.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { tianGanWuXing, diZhiBenQiWuXing } from "@/engine/v299/m02_batch01_reference";
import { fiveElementAuxLookup } from "@/engine/v299/m02_batch03_reference";
import { yueLingWeightByBranchAndElement } from "@/engine/v299/m02_batch07_reference";
import {
  QI_LEI_TOKEN,
  zhanKaiLookupBy,
  zhanKaiHasKey,
} from "@/engine/v299/m02_batch06_reference";

// ── Audited constants (Unicode-authoritative) ──────────────────────────

const KEY_SEP = "_";
const EMPTY = "";

// Five elements (audited Unicode; identical to Batch03's EL constants).
const EL_WOOD  = String.fromCodePoint(26408); // 木
const EL_FIRE  = String.fromCodePoint(28779); // 火
const EL_EARTH = String.fromCodePoint(22303); // 土
const EL_METAL = String.fromCodePoint(37329); // 金
const EL_WATER = String.fromCodePoint(27700); // 水

/**
 * Audited five-element enumeration order used by A32:A36 (i.e. rows 32..36
 * of Sheet28). This ordering is authoritative for INDEX/MATCH semantics
 * inside B53 (`INDEX(A32:A36, MATCH(MIN(B32:B36), B32:B36, 0))`).
 */
export const FIVE_ELEMENT_ORDER = Object.freeze([
  EL_WOOD,
  EL_FIRE,
  EL_EARTH,
  EL_METAL,
  EL_WATER,
]);

// Support-flag output tokens (Unicode-authoritative).
const B42_DE_LING     = String.fromCodePoint(24471, 20196);       // 得令
const B42_BU_DE_LING  = String.fromCodePoint(19981, 24471, 20196); // 不得令
const B43_DE_DI       = String.fromCodePoint(24471, 22320);       // 得地
const B43_BU_DE_DI    = String.fromCodePoint(19981, 24471, 22320); // 不得地
const B44_DE_ZHU      = String.fromCodePoint(24471, 21161);       // 得助
const B44_BU_DE_ZHU   = String.fromCodePoint(19981, 24471, 21161); // 不得助

// Status tokens (B52).
const B52_PIAN_RUO  = String.fromCodePoint(20559, 24369); // 偏弱
const B52_PIAN_QIANG = String.fromCodePoint(20559, 24378); // 偏强
const B52_PING_HENG = String.fromCodePoint(24179, 34913); // 平衡

// Need prefix (B53).
const B53_BU_PREFIX = String.fromCodePoint(34917); // 补

// Tie tokens (B54).
const B54_WU_BING_LIE = String.fromCodePoint(26080, 24182, 21015); // 无并列
// Excel warning template segments per authoritative evidence:
//   "⚠️ 并列提示：" + <N> + "个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。"
// Segment 1 (prefix): "⚠️ 并列提示：" Unicode [9888,65039,32,24182,21015,25552,31034,65306]
const B54_WARN_PREFIX = String.fromCodePoint(
  9888, 65039, 32, 24182, 21015, 25552, 31034, 65306
);
// Segment 2 (suffix): "个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。"
// Unicode [20010,20803,32032,21516,20026,26368,20302,65292,24314,35758,22312,
//          20027,35201,34917,30410,26041,21521,22522,30784,19978,20860,39038,
//          20854,20182,20559,24369,20803,32032,12290]
const B54_WARN_SUFFIX = String.fromCodePoint(
  20010, 20803, 32032, 21516, 20026, 26368, 20302, 65292,
  24314, 35758, 22312, 20027, 35201, 34917, 30410, 26041,
  21521, 22522, 30784, 19978, 20860, 39038, 20854, 20182,
  20559, 24369, 20803, 32032, 12290
);

// ── Type guards ────────────────────────────────────────────────────────

function requireStemArg(name, v) {
  if (typeof v !== "string" || v.length === 0) {
    throw new TypeError(`[M04·Sheet28] ${name} must be an audited stem glyph.`);
  }
  return v;
}
function requireBranchArg(name, v) {
  if (typeof v !== "string" || v.length === 0) {
    throw new TypeError(`[M04·Sheet28] ${name} must be an audited branch glyph.`);
  }
  return v;
}

// ── IFERROR-wrapped hidden-stem lookup (returns null on miss) ──────────

function hiddenStemRowIfPresent(branch, position) {
  const key = branch + KEY_SEP + position;
  if (!zhanKaiHasKey(key)) return null;
  return zhanKaiLookupBy(branch, position);
}

// ── Weighted matrix row builders ───────────────────────────────────────

/**
 * Compute a single 天干 row (B13..B16). Base weight D is static 5;
 * E is looked up from DB_月令权重 by (monthBranch, C-column element).
 */
function buildTianGanRow(label, tianGan, monthBranch) {
  const element = tianGanWuXing(tianGan);
  const base = 5;
  const coefficient = yueLingWeightByBranchAndElement(monthBranch, element);
  return Object.freeze({
    label,
    B: tianGan,
    C: element,
    D: base,
    E: coefficient,
    F: base * coefficient,
  });
}

/**
 * Compute a single 地支藏干 row (B17..B28). Missing hidden-stem key
 * → audited defaults (B="", C="", D=0, E=0, F=0).
 */
function buildDiZhiCangGanRow(label, branch, position, monthBranch) {
  const row = hiddenStemRowIfPresent(branch, position);
  if (row === null) {
    return Object.freeze({
      label,
      B: EMPTY,
      C: EMPTY,
      D: 0,
      E: 0,
      F: 0,
    });
  }
  const D = row.quanZhong;
  // Excel semantics: IF(D=0, 0, VLOOKUP(...)) — a present hidden-stem row
  // with quanZhong=0 short-circuits E to 0. Defensive; not expected in
  // authoritative data.
  if (D === 0) {
    return Object.freeze({
      label,
      B: row.cangGan,
      C: row.cangGanWuXing,
      D: 0,
      E: 0,
      F: 0,
    });
  }
  const E = yueLingWeightByBranchAndElement(monthBranch, row.cangGanWuXing);
  return Object.freeze({
    label,
    B: row.cangGan,
    C: row.cangGanWuXing,
    D,
    E,
    F: D * E,
  });
}

// Fixed audited labels — retained here for downstream reporting only.
// (Sheet28 A13..A28 wording, Unicode-authoritative.)
const LABEL_NIAN_GAN     = String.fromCodePoint(24180, 24178);            // 年干
const LABEL_YUE_GAN      = String.fromCodePoint(26376, 24178);            // 月干
const LABEL_RI_GAN       = String.fromCodePoint(26085, 24178);            // 日干
const LABEL_SHI_GAN      = String.fromCodePoint(26102, 24178);            // 时干
const LABEL_NIAN_BEN     = String.fromCodePoint(24180, 25903, 95, 26412, 27668); // 年支_本气
const LABEL_NIAN_ZHONG   = String.fromCodePoint(24180, 25903, 95, 20013, 27668); // 年支_中气
const LABEL_NIAN_YU      = String.fromCodePoint(24180, 25903, 95, 20313, 27668); // 年支_余气
const LABEL_YUE_BEN      = String.fromCodePoint(26376, 25903, 95, 26412, 27668); // 月支_本气
const LABEL_YUE_ZHONG    = String.fromCodePoint(26376, 25903, 95, 20013, 27668); // 月支_中气
const LABEL_YUE_YU       = String.fromCodePoint(26376, 25903, 95, 20313, 27668); // 月支_余气
const LABEL_RI_BEN       = String.fromCodePoint(26085, 25903, 95, 26412, 27668); // 日支_本气
const LABEL_RI_ZHONG     = String.fromCodePoint(26085, 25903, 95, 20013, 27668); // 日支_中气
const LABEL_RI_YU        = String.fromCodePoint(26085, 25903, 95, 20313, 27668); // 日支_余气
const LABEL_SHI_BEN      = String.fromCodePoint(26102, 25903, 95, 26412, 27668); // 时支_本气
const LABEL_SHI_ZHONG    = String.fromCodePoint(26102, 25903, 95, 20013, 27668); // 时支_中气
const LABEL_SHI_YU       = String.fromCodePoint(26102, 25903, 95, 20313, 27668); // 时支_余气

// ── Main compute ───────────────────────────────────────────────────────

/**
 * Compute the Batch 07-2 minimum M04/Sheet28 output slice from the four
 * audited pillar inputs + upstream boundary summary.
 *
 * @param {{
 *   yearStem: string,     // B5 ← 'BZ_自动排盘'!B36
 *   yearBranch: string,   // D5 ← 'BZ_自动排盘'!B37
 *   monthStem: string,    // B6 ← 'BZ_自动排盘'!B45
 *   monthBranch: string,  // D6 ← 'BZ_自动排盘'!B46 ; B9 == D6
 *   dayStem: string,      // B7 ← 'BZ_自动排盘'!B52 (day master)
 *   dayBranch: string,    // D7 ← 'BZ_自动排盘'!B54
 *   hourStem: string,     // B8 ← 'BZ_自动排盘'!B61
 *   hourBranch: string,   // D8 ← 'BZ_自动排盘'!B62
 *   boundarySummary: string, // B55 ← 'BZ_自动排盘'!B71 passthrough
 * }} inputs
 */
export function computeSheet28Min(inputs) {
  if (inputs === null || typeof inputs !== "object") {
    throw new TypeError("[M04·Sheet28] inputs must be an object.");
  }
  const {
    yearStem,
    yearBranch,
    monthStem,
    monthBranch,
    dayStem,
    dayBranch,
    hourStem,
    hourBranch,
    boundarySummary,
  } = inputs;
  requireStemArg("yearStem",  yearStem);
  requireBranchArg("yearBranch",  yearBranch);
  requireStemArg("monthStem", monthStem);
  requireBranchArg("monthBranch", monthBranch);
  requireStemArg("dayStem",   dayStem);
  requireBranchArg("dayBranch",   dayBranch);
  requireStemArg("hourStem",  hourStem);
  requireBranchArg("hourBranch",  hourBranch);
  if (typeof boundarySummary !== "string") {
    throw new TypeError(
      "[M04·Sheet28] boundarySummary (upstream B71) must be a string; " +
        "pass '' for the no-boundary branch."
    );
  }

  // B9 = D6 (month branch reused for 月令 coefficient lookups).
  const B9 = monthBranch;

  // ── Rows 13..16 (天干) ───────────────────────────────────────────
  const r13 = buildTianGanRow(LABEL_NIAN_GAN, yearStem,  B9);
  const r14 = buildTianGanRow(LABEL_YUE_GAN,  monthStem, B9);
  const r15 = buildTianGanRow(LABEL_RI_GAN,   dayStem,   B9);
  const r16 = buildTianGanRow(LABEL_SHI_GAN,  hourStem,  B9);

  // ── Rows 17..28 (地支藏干) ─────────────────────────────────────
  const r17 = buildDiZhiCangGanRow(LABEL_NIAN_BEN,   yearBranch,  QI_LEI_TOKEN.BEN_QI,   B9);
  const r18 = buildDiZhiCangGanRow(LABEL_NIAN_ZHONG, yearBranch,  QI_LEI_TOKEN.ZHONG_QI, B9);
  const r19 = buildDiZhiCangGanRow(LABEL_NIAN_YU,    yearBranch,  QI_LEI_TOKEN.YU_QI,    B9);
  const r20 = buildDiZhiCangGanRow(LABEL_YUE_BEN,    monthBranch, QI_LEI_TOKEN.BEN_QI,   B9);
  const r21 = buildDiZhiCangGanRow(LABEL_YUE_ZHONG,  monthBranch, QI_LEI_TOKEN.ZHONG_QI, B9);
  const r22 = buildDiZhiCangGanRow(LABEL_YUE_YU,     monthBranch, QI_LEI_TOKEN.YU_QI,    B9);
  const r23 = buildDiZhiCangGanRow(LABEL_RI_BEN,     dayBranch,   QI_LEI_TOKEN.BEN_QI,   B9);
  const r24 = buildDiZhiCangGanRow(LABEL_RI_ZHONG,   dayBranch,   QI_LEI_TOKEN.ZHONG_QI, B9);
  const r25 = buildDiZhiCangGanRow(LABEL_RI_YU,      dayBranch,   QI_LEI_TOKEN.YU_QI,    B9);
  const r26 = buildDiZhiCangGanRow(LABEL_SHI_BEN,    hourBranch,  QI_LEI_TOKEN.BEN_QI,   B9);
  const r27 = buildDiZhiCangGanRow(LABEL_SHI_ZHONG,  hourBranch,  QI_LEI_TOKEN.ZHONG_QI, B9);
  const r28 = buildDiZhiCangGanRow(LABEL_SHI_YU,     hourBranch,  QI_LEI_TOKEN.YU_QI,    B9);

  const weightedRows = Object.freeze([
    r13, r14, r15, r16, r17, r18, r19, r20, r21, r22, r23, r24, r25, r26, r27, r28,
  ]);

  // ── Rows 32..37 (totals / ratios) ──────────────────────────────
  const sumFor = (el) =>
    weightedRows.reduce((s, r) => (r.C === el ? s + r.F : s), 0);
  const totalMu    = sumFor(EL_WOOD);
  const totalHuo   = sumFor(EL_FIRE);
  const totalTu    = sumFor(EL_EARTH);
  const totalJin   = sumFor(EL_METAL);
  const totalShui  = sumFor(EL_WATER);
  const total      = totalMu + totalHuo + totalTu + totalJin + totalShui;

  const ratio = (n) => (total === 0 ? 0 : n / total);
  const elementTotals = Object.freeze({
    [EL_WOOD]:  totalMu,
    [EL_FIRE]:  totalHuo,
    [EL_EARTH]: totalTu,
    [EL_METAL]: totalJin,
    [EL_WATER]: totalShui,
  });
  const elementRatios = Object.freeze({
    [EL_WOOD]:  ratio(totalMu),
    [EL_FIRE]:  ratio(totalHuo),
    [EL_EARTH]: ratio(totalTu),
    [EL_METAL]: ratio(totalJin),
    [EL_WATER]: ratio(totalShui),
  });

  // ── B50 day-master element ────────────────────────────────────
  // Excel: B50 = $C$15 (i.e. row 15 = 日干 五行).
  const dayMasterElement = r15.C;

  // ── B51 day-master ratio = IFERROR(VLOOKUP(B50, A32:C36, 3, FALSE), 0) ─
  // Column 3 of (A32:C36) is C — the ratio column.
  const dayMasterRatio = elementRatios[dayMasterElement] ?? 0;

  // ── B42..B44 support flags ────────────────────────────────────
  // dayMasterElement C15 vs 月支本气五行 (VLOOKUP D6 in DB_地支!B:E col 2)
  // OR VLOOKUP(C15, DB_十神!A:E, 4) which is `shengWoYin` (印).
  const monthBranchBenQiElement = diZhiBenQiWuXing(monthBranch);
  const printElement = fiveElementAuxLookup(dayMasterElement).shengWoYin; // "印"

  const B42 =
    monthBranchBenQiElement === dayMasterElement ||
    monthBranchBenQiElement === printElement
      ? B42_DE_LING
      : B42_BU_DE_LING;

  // B43 得地：任一 C17..C28 藏干五行 ∈ {日主, 印}
  const branchElementRows = [r17, r18, r19, r20, r21, r22, r23, r24, r25, r26, r27, r28];
  const B43 =
    branchElementRows.some(
      (r) => r.C === dayMasterElement || r.C === printElement
    )
      ? B43_DE_DI
      : B43_BU_DE_DI;

  // B44 得助：C13/C14/C16 ∈ {日主, 印}
  const otherStemRows = [r13, r14, r16];
  const B44 =
    otherStemRows.some(
      (r) => r.C === dayMasterElement || r.C === printElement
    )
      ? B44_DE_ZHU
      : B44_BU_DE_ZHU;

  // ── B45 strength score ────────────────────────────────────────
  const roundedRatioTimes100 = round1(dayMasterRatio * 100);
  const strengthScore =
    roundedRatioTimes100 +
    (B42 === B42_DE_LING ? 10 : 0) +
    (B43 === B43_DE_DI   ? 10 : 0) +
    (B44 === B44_DE_ZHU  ? 5  : 0);

  // ── B52 status ────────────────────────────────────────────────
  const status =
    strengthScore < 35 ? B52_PIAN_RUO :
    strengthScore > 65 ? B52_PIAN_QIANG :
    B52_PING_HENG;

  // ── B53 need + B54 tie ────────────────────────────────────────
  const totalsInOrder = FIVE_ELEMENT_ORDER.map((el) => elementTotals[el]);
  let need, tie;
  if (total === 0) {
    need = EMPTY;
    tie = EMPTY;
  } else {
    const minVal = Math.min(...totalsInOrder);
    const firstMinIdx = totalsInOrder.indexOf(minVal);
    need = B53_BU_PREFIX + FIVE_ELEMENT_ORDER[firstMinIdx];
    const tieCount = totalsInOrder.filter((v) => v === minVal).length;
    tie =
      tieCount > 1
        ? B54_WARN_PREFIX + String(tieCount) + B54_WARN_SUFFIX
        : B54_WU_BING_LIE;
  }

  // ── B55 boundary passthrough ─────────────────────────────────
  const boundary = boundarySummary;

  return Object.freeze({
    inputs: Object.freeze({
      B5: yearStem,  C5: LABEL_NIAN_GAN, // header not exposed as compute output
      D5: yearBranch,
      B6: monthStem,  D6: monthBranch,
      B7: dayStem,    D7: dayBranch,
      B8: hourStem,   D8: hourBranch,
      B9,
    }),
    weightedRows,
    elementTotals,
    elementRatios,
    total,                              // B37
    supportFlags: Object.freeze({
      B42, B43, B44,
    }),
    strengthScore,                      // B45
    dayMasterElement,                   // B50
    dayMasterRatio,                     // B51
    status,                             // B52
    need,                               // B53
    tie,                                // B54
    boundary,                           // B55
    _provenance: Object.freeze({
      excelSource: "Sheet28 (BZ_五行强弱) A1:F55 minimum subset",
      formulaFamily: {
        row13_16: "B=stem, C=DB_天干!D, D=5, E=DB_月令权重!<C-col>, F=D*E",
        row17_28: "B/C/D=DB_藏干 cols4/5/6 (IFERROR→'',''.0), E=IF(D=0,0,DB_月令权重!<C-col>), F=D*E",
        totals32_36: "SUMIF(C13:C28, <element>, F13:F28)",
        ratios32_36: "IF(B37=0, 0, Bi/B37)",
        total37: "SUM(B32:B36)",
        b42: "月支本气五行 ∈ {日主, 印} → 得令 else 不得令",
        b43: "任一 C17..C28 ∈ {日主, 印} → 得地 else 不得地",
        b44: "任一 C13/C14/C16 ∈ {日主, 印} → 得助 else 不得助",
        b45: "ROUND(B51*100,1) + IF(B42=得令,10,0) + IF(B43=得地,10,0) + IF(B44=得助,5,0)",
        b50: "$C$15",
        b51: "IFERROR(VLOOKUP(B50, A32:C36, 3, FALSE), 0)",
        b52: "IF(B45<35,偏弱, IF(B45>65,偏强, 平衡))",
        b53: "IF(B37=0,'', '补'&INDEX(A32:A36, MATCH(MIN(B32:B36), B32:B36, 0)))",
        b54: "IF(B37=0,'', IF(COUNTIF(B32:B36, MIN(B32:B36))>1, <warn template>, 无并列))",
        b55: "'BZ_自动排盘'!B71 passthrough",
      },
      goldenMasterHash:
        "6d97148d708a3dbc84bcec9bdce9facdb815891de077036c9fc118a75e86d500",
    }),
  });
}

/** Excel ROUND(x, 1) — banker-rounding is NOT used; standard half-away-from-zero. */
function round1(x) {
  // Excel ROUND rounds half away from zero. JS Math.round uses half-to-+Inf.
  // For positive numbers relevant to Sheet28 they agree; for negatives
  // preserve Excel semantics defensively.
  const sign = x < 0 ? -1 : 1;
  const abs = Math.abs(x);
  return sign * Math.round(abs * 10) / 10;
}

/**
 * Exact-Excel warning template renderer for B54 tie case.
 * Public for testability; NOT expected to be called by the composer
 * directly since `computeSheet28Min` embeds the same logic.
 */
export function renderB54Warning(tieCount) {
  if (!Number.isInteger(tieCount) || tieCount < 2) {
    throw new RangeError(
      "[M04·Sheet28] renderB54Warning tieCount must be an integer >= 2."
    );
  }
  return B54_WARN_PREFIX + String(tieCount) + B54_WARN_SUFFIX;
}

// Public re-exports for authoritative regression tests / documentation.
export const AUDITED_TOKENS = Object.freeze({
  B42_DE_LING, B42_BU_DE_LING,
  B43_DE_DI, B43_BU_DE_DI,
  B44_DE_ZHU, B44_BU_DE_ZHU,
  B52_PIAN_RUO, B52_PIAN_QIANG, B52_PING_HENG,
  B53_BU_PREFIX,
  B54_WU_BING_LIE, B54_WARN_PREFIX, B54_WARN_SUFFIX,
  FIVE_ELEMENT_ORDER,
});
